// src/Components/HomePage.jsx
import React, { useEffect, useState, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useHistory } from "./HistoryContext";
import Header from "./Header.jsx";
import "./HomePage.css";

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const VideoCard = memo(({ video, onSelect }) => (
  <div
    className="video-card fade-in"
    onClick={() => onSelect(video.src, video.title)}
    role="button"
    tabIndex={0}
    onKeyPress={(e) => e.key === "Enter" && onSelect(video.src, video.title)}
    aria-label={`Play ${video.title}`}
  >
    <div className="thumbnail-container">
      <img
        src={video.thumbnail || "https://via.placeholder.com/300x200?text=No+Thumbnail"}
        alt={`${video.title} thumbnail`}
        loading="lazy"
        onLoad={(e) => e.target.classList.add("loaded")}
        onError={(e) => {
          e.target.src = "https://via.placeholder.com/300x200?text=No+Thumbnail";
          e.target.classList.add("loaded");
        }}
      />
    </div>
    <div className="video-details">
      <h4 className="homepage-video-title">{video.title}</h4>
      <span className="video-duration">Duration: {video.duration || "Unknown"}</span>
    </div>
  </div>
));

const HomePage = () => {
  const navigate = useNavigate();
  const { addToHistory } = useHistory();
  const [videos, setVideos] = useState([]);
  const [visibleVideos, setVisibleVideos] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [error, setError] = useState(null);
  const theme = localStorage.getItem("theme") || "light";
  const loaderRef = useRef(null);
  const [showImage, setShowImage] = useState(false);
  const VIDEOS_PER_PAGE = 6;

  // Core fetch function with enhanced error handling, multi-language support, and shuffling
  const fetchVideos = async (language, category, type) => {
    console.log("🎥 Fetching videos with:", { language, category, type });
    setIsFetchingData(true);
    setError(null);
    setVideos([]);
    setVisibleVideos([]);

    try {
      // Normalize inputs
      const safeCategory = category || "all";
      const safeType = type || "videos";
      const languages = language === "Both" ? ["English", "Hindi"] : [language || "English"];

      let allVids = [];
      for (const lang of languages) {
        const filePath = `/API/Videos/${lang}${safeType.charAt(0).toUpperCase() + safeType.slice(1)}.json`;
        console.log(`Fetching from: ${filePath}`);
        const response = await fetch(filePath);

        if (!response.ok) {
          throw new Error(`HTTP error for ${lang}: ${response.status} ${response.statusText}`);
        }

        let data;
        let rawText = "";
        try {
          rawText = await response.text();
          if (!rawText) throw new Error(`Empty response from server for ${lang}`);
          data = JSON.parse(rawText);
        } catch (err) {
          console.error(`Raw response text for ${lang}:`, rawText || "(empty)");
          throw new Error(`Invalid JSON format for ${lang}: ${err.message}`);
        }

        // Validate data structure
        if (!data || typeof data !== "object") {
          throw new Error(`Response for ${lang} is not a valid object`);
        }

        // Normalize data keys to lowercase
        const normalized = {};
        Object.keys(data).forEach((key) => {
          normalized[key.toLowerCase()] = Array.isArray(data[key]) ? data[key] : [];
        });

        // Handle categories
        const selectedCats = Array.isArray(safeCategory)
          ? safeCategory.map((c) => String(c).toLowerCase())
          : [String(safeCategory).toLowerCase()];

        let vids = [];
        if (selectedCats.includes("all")) {
          Object.values(normalized).forEach((arr) => vids.push(...arr));
        } else {
          selectedCats.forEach((cat) => {
            if (normalized[cat]) vids.push(...normalized[cat]);
          });
        }

        // Add language to video objects for clarity
        vids = vids
          .filter((v) => v && v.src && v.title && v.thumbnail && v.duration)
          .map((v) => ({ ...v, language: lang }));

        allVids.push(...vids);
      }

      if (allVids.length === 0) {
        console.warn("No valid videos found in response");
      }

      // Shuffle the videos array
      const shuffledVideos = shuffleArray(allVids);

      setVideos(shuffledVideos);
      setVisibleVideos(shuffledVideos.slice(0, VIDEOS_PER_PAGE));
      setPage(1);
      console.log(`✅ Loaded and shuffled ${shuffledVideos.length} videos for category "${safeCategory}" and language(s) "${languages.join(", ")}"`);
    } catch (err) {
      console.error("❌ Error fetching videos:", err);
      setError(`Failed to load videos: ${err.message}`);
    } finally {
      setIsFetchingData(false);
    }
  };

  // Detect Android + Chrome and manage image visibility
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroidChrome =
      /android/.test(userAgent) &&
      /chrome/.test(userAgent) &&
      !/firefox|opera|ucbrowser|samsungbrowser/.test(userAgent);
    const hasSeenImage = localStorage.getItem("vitstra-addtip") === "seen";

    if (isAndroidChrome && !hasSeenImage) {
      const timer = setTimeout(() => {
        setShowImage(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle image dismissal
  const handleDismissImage = () => {
    localStorage.setItem("vitstra-addtip", "seen");
    setShowImage(false);
  };

  // React to "vitstra-update" event
  useEffect(() => {
    const handleUpdate = (e) => {
      try {
        const lang = localStorage.getItem("userLanguage") || "English";
        const cat = JSON.parse(localStorage.getItem("vitstraCategory") || '"all"');
        const type = "videos"; // Hardcode to videos since shorts are disabled
        console.log("🔄 vitstra-update event detected:", { lang, cat, type });
        fetchVideos(lang, cat, type);
      } catch (err) {
        console.error("Error handling vitstra-update:", err);
        setError("Failed to update video feed.");
      }
    };
    window.addEventListener("vitstra-update", handleUpdate);
    return () => window.removeEventListener("vitstra-update", handleUpdate);
  }, []);

  // Load once on startup
  useEffect(() => {
    const lang = localStorage.getItem("userLanguage") || "English";
    let cat;
    try {
      cat = JSON.parse(localStorage.getItem("vitstraCategory") || '"all"');
    } catch {
      cat = "all";
    }
    const type = "videos"; // Hardcode to videos
    fetchVideos(lang, cat, type);
  }, []);

  // Sync with language changes from Header
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "userLanguage") {
        const lang = e.newValue || "English";
        const cat = JSON.parse(localStorage.getItem("vitstraCategory") || '"all"');
        const type = "videos";
        console.log("🔄 Language changed:", { lang, cat, type });
        fetchVideos(lang, cat, type);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Infinite scroll
  useEffect(() => {
    const el = loaderRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && visibleVideos.length < videos.length) {
          setIsLoading(true);
          setTimeout(() => {
            const next = page + 1;
            setVisibleVideos((p) => [...p, ...videos.slice(p.length, p.length + VIDEOS_PER_PAGE)]);
            setPage(next);
            setIsLoading(false);
          }, 400);
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );
    if (el) obs.observe(el);
    return () => el && obs.unobserve(el);
  }, [isLoading, page, videos, visibleVideos.length]);

  // Handle video click
  const handleVideoSelect = (src, title) => {
    const fullSrc = src.includes("youtube.com")
      ? `${src}${src.includes("?") ? "&" : "?"}autoplay=1&mute=0`
      : src;
    addToHistory({ src: fullSrc, title });
    navigate(`/video/${encodeURIComponent(title)}`, { state: { videoSrc: fullSrc, videoTitle: title } });
  };

  return (
    <div className={`homepage ${theme}-theme`}>
      <Header />
      <div className="video-grid-container">
        {isFetchingData ? (
          <div className="loading-indicator"><p>Loading videos...</p></div>
        ) : error ? (
          <div className="error-message"><p>{error}</p></div>
        ) : visibleVideos.length === 0 ? (
          <div className="no-videos-message"><p>No videos found.</p></div>
        ) : (
          <>
            <div className="video-grid">
              {visibleVideos.map((v, i) => (
                <VideoCard key={`${v.language}-${v.src}-${i}`} video={v} onSelect={handleVideoSelect} />
              ))}
            </div>
            <div ref={loaderRef} className="loading-indicator">
              {isLoading && <p>Loading more...</p>}
            </div>
          </>
        )}
      </div>
      {showImage && (
        <div className="add-to-home-container">
          <button
            className="add-to-home-close"
            onClick={handleDismissImage}
            aria-label="Close add to home screen tip"
          >
            ❌
          </button>
          <img
            src="/icons/VitStra_add_to_homescreen.png"
            alt="Add VitStra to Home Screen"
            className="add-to-home-image"
          />
        </div>
      )}
    </div>
  );
};

export default memo(HomePage);